import { useFormik } from 'formik';
import { Button } from '../../components/buttons/Button';
import { useSearchParams } from 'react-router';
import { useState } from 'react';
import Alert from '../../components/Alert.tsx';
import API, { API_URL } from '../../lib/api.ts';
import {
    cn,
    PUBLIC_EMAIL_DOMAINS,
    transformLaravelToFormikErrors,
} from '../../lib/helpers.ts';
import { SchemaDrivenFormFieldProps } from '../../lib/types.ts';
import * as Yup from 'yup';
import { renderSchemaForm } from '../../components/forms/renderSchemaForm.tsx';

export default function SignUpForm({ className }: { className?: string }) {
    const [passwordResetStatus, setPasswordResetStatus] = useState<
        undefined | string
    >(undefined);

    const [searchParams] = useSearchParams();

    const validationSchema = Yup.object().shape({
        first_name: Yup.string()
            .min(2, 'Vyplňte prosím své jméno.')
            .required('Jméno je povinné pole.'),
        last_name: Yup.string()
            .min(2, 'Vyplňte prosím své příjmení.')
            .required('Příjmení je povinné pole.'),
        school_role: Yup.string()
            .oneOf([
                'vedeni-skoly',
                'pracovnik-spp',
                'pedagogicky-pracovnik',
                'jina-instituce',
            ])
            .required('Role je povinné pole.'),
        email: Yup.string()
            .email('E-mail není ve správném formátu.')
            .test(
                'email-domain',
                'Zdá se, že jste nezadali svůj pracovní e-mail. Potřebujeme vaši registraci ověřit, prosím klikněte na "jiná instituce" a vyplňte své údaje ručně.',
                (value) => {
                    // This applies only for school registration (not for other) - check values.institution_is_a_school
                    if (!values.institution_is_a_school) return true;

                    const domain = value?.split('@')[1]?.toLowerCase();
                    if (!domain) return false;

                    return !PUBLIC_EMAIL_DOMAINS.includes(domain);
                }
            )
            .required('E-mail je povinné pole.'),
        phone: Yup.string()
            .matches(
                /^(\+42[01] ?)?\d{3} ?\d{3} ?\d{3}$/,
                'Telefonní číslo není ve správném formátu.'
            )
            .required('Telefonní číslo je povinné pole.'),
        password: Yup.string()
            .min(8, 'Heslo musí mít alespoň 8 znaků.')
            .required('Heslo je povinné pole.'),
        password_confirmation: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Hesla se neshodují.')
            .required('Heslo je povinné pole.'),
        institution: Yup.string().required('Název školy je povinné pole.'),
        institution_ico: Yup.string(),
        institution_street: Yup.string().required('Ulice je povinné pole.'),
        institution_city: Yup.string().required('Město je povinné pole.'),
        institution_postal_code: Yup.string().required('PSČ je povinné pole.'),
        // license_confirmation: Yup.boolean().oneOf(
        //     [true],
        //     'Musíte souhlasit s licenční smlouvou.'
        // ),
        tos_gdpr_confirmation: Yup.boolean().oneOf(
            [true],
            'Musíte souhlasit s podmínkami užití a zásadami ochrany osobních údajů.'
        ),
        newsletter_confirmation: Yup.boolean(),
        registration_code: Yup.string(),
    });

    const {
        handleSubmit,
        handleBlur,
        handleChange,
        values,
        setSubmitting,
        isSubmitting,
        errors,
        touched,
        setErrors,
        setFieldValue,
        setValues,
        validateField,
    } = useFormik({
        validateOnChange: true,
        validateOnBlur: true,
        initialValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            password: '',
            password_confirmation: '',
            //
            institution_is_a_school: true,
            institution: '',
            institution_id: null,
            institution_ico: '',
            institution_street: '',
            institution_city: '',
            institution_postal_code: '',
            //
            // license: '',
            tos_gdpr_confirmation: false,
            newsletter_confirmation: false,
            //
            registration_code: searchParams.get('rc') ?? '',
            //
            // first_name: 'Kocour',
            // last_name: 'Všímavec',
            // email: 'ondrca@gmail.com',
            // phone: '123456789',
            // password: 'testtest',
            // password_confirmation: 'testtest',
            // //
            // institution: 'ZŠ Nějaká',
            // institution_ico: '123456',
            // institution_street: 'Nějaká 123',
            // institution_city: 'Město',
            // institution_postal_code: '53001',
            // //
            // license: '',
            // //
            // registration_code: searchParams.get('rc') ?? '',
        },
        validationSchema,
        onSubmit: (values) => {
            // Take only numbers from institution_ico, phone, institution_postal_code
            setValues(
                {
                    ...values,
                    institution_ico: values.institution_ico.replace(/\D/g, ''),
                    phone: values.phone.replace(/\D/g, ''),
                    institution_postal_code:
                        values.institution_postal_code.replace(/\D/g, ''),
                },
                false
            );

            API.post('/register', {
                ...values,
                institution_ico: values.institution_ico.length
                    ? values.institution_ico
                    : null,
            })
                .then((res) => {
                    // @ts-ignore
                    window.dataLayer?.push({
                        event: 'registration',
                    });

                    setPasswordResetStatus(
                        res?.data?.status ?? 'Registrace byla úspěšná.'
                    );
                    // setTimeout(() => {
                    //     navigate('/dashboard');
                    // }, 5000);
                })
                .catch((err) => {
                    const errors = err?.response?.data?.errors;
                    if (errors)
                        setErrors(transformLaravelToFormikErrors(errors));
                })
                .finally(() => setSubmitting(false));
        },
    });

    const schema: SchemaDrivenFormFieldProps[] = [
        // ---------------------------------------------------------------------
        {
            name: 'section-institution',
            type: 'heading',
            className: '!mt-0',
            label: (
                <>
                    {values.institution_is_a_school
                        ? 'Název školy'
                        : 'Údaje o instituci'}{' '}
                    {values.institution_is_a_school && (
                        <span className="text-red-500">*</span>
                    )}
                </>
            ),
        },
        {
            name: 'institution',
            type: values.institution_is_a_school ? 'autocomplete' : 'text',
            label: values.institution_is_a_school ? null : 'Název instituce',
        },
        {
            name: 'institution_ico',
            type: values.institution_is_a_school ? 'hidden' : 'text',
            label: 'IČO',
            disabled: values.institution_is_a_school,
            required: false,
        },
        {
            name: 'institution_street',
            type: values.institution_is_a_school ? 'hidden' : 'text',
            label: 'Ulice',
            disabled: values.institution_is_a_school,
        },
        {
            name: 'institution-address',
            type: values.institution_is_a_school ? 'hidden' : 'grid',
            content: [
                {
                    name: 'institution_city',
                    type: values.institution_is_a_school ? 'hidden' : 'text',
                    label: 'Město',
                    disabled: values.institution_is_a_school,
                },
                {
                    name: 'institution_postal_code',
                    type: values.institution_is_a_school ? 'hidden' : 'text',
                    label: 'PSČ',
                    disabled: values.institution_is_a_school,
                },
            ],
        },
        {
            name: 'section-school-sub',
            type: 'subheading',
            label: (
                <p className="mt-1">
                    {values.institution_is_a_school
                        ? 'Jste jiná instituce než škola nebo jste se nenašli v seznamu?'
                        : 'Jste škola?'}{' '}
                    <a
                        className="cursor-pointer text-blue-500 underline"
                        onClick={() => {
                            setFieldValue(
                                'institution_is_a_school',
                                !values.institution_is_a_school
                            ).then(() => {
                                validateField('email');
                            });
                        }}
                    >
                        {values.institution_is_a_school
                            ? 'Zadejte své informace ručně'
                            : 'Vyhledejte se v seznamu'}
                    </a>
                </p>
            ),
        },
        // ---------------------------------------------------------------------
        {
            name: 'section-contact-person',
            type: 'heading',
            label: 'Kontaktní osoba',
        },
        {
            name: 'name-surname',
            type: 'grid',
            content: [
                {
                    name: 'first_name',
                    type: 'text',
                    label: 'Jméno',
                },
                {
                    name: 'last_name',
                    type: 'text',
                    label: 'Příjmení',
                },
            ],
        },
        {
            name: 'school_role',
            type: 'select',
            label: 'Jaká je vaše role?',
            options: [
                {
                    label: 'Ředitel / Zástupce',
                    value: 'vedeni-skoly',
                },
                {
                    label: 'Pracovník ŠPP',
                    value: 'pracovnik-spp',
                },
                {
                    label: 'Pedagogický pracovník',
                    value: 'pedagogicky-pracovnik',
                },
                {
                    label: 'Jiná instituce',
                    value: 'jina-instituce',
                },
            ],
        },
        {
            name: 'email-phone',
            type: 'grid',
            content: [
                {
                    name: 'email',
                    type: 'email',
                    label: 'Pracovní e-mail',
                    placeholder: 'email@skola.cz',
                },
                {
                    name: 'phone',
                    type: 'tel',
                    label: 'Telefonní číslo',
                    placeholder: '+420 123 456 789',
                },
            ],
        },
        {
            name: 'password-password-again',
            type: 'grid',
            content: [
                {
                    name: 'password',
                    type: 'password',
                    label: 'Heslo',
                },
                {
                    name: 'password_confirmation',
                    type: 'password',
                    label: 'Heslo znovu',
                },
            ],
        },
        // ---------------------------------------------------------------------
        // {
        //     name: 'section-license',
        //     type: 'heading',
        //     label: 'Licence',
        // },
        // {
        //     name: 'license',
        //     type: 'license',
        //     label: 'Licence',
        // },
        // {
        //     name: 'license_confirmation',
        //     type: 'checkbox',
        //     label: (
        //         <span>
        //             Zaškrtnutím výslovně souhlasím s podmínkami{' '}
        //             <a
        //                 href={`${API_URL}/../files/vsimavec-licencni-smlouva-pro-skoly.pdf`}
        //                 target="_blank"
        //                 className="text-blue-500 underline"
        //             >
        //                 licenční smlouvy
        //             </a>
        //             , kterou jsem si přečetl a bez výhrad ji přijímám.
        //         </span>
        //     ),
        // },
        {
            name: 'section-tos',
            type: 'heading',
            label: 'Podmínky užití',
        },
        {
            name: 'tos_gdpr_confirmation',
            type: 'checkbox',
            label: (
                <>
                    Souhlasím s{' '}
                    <a
                        href={`${API_URL}/../files/vsimavec-obchodni-podminky.pdf`}
                        target="_blank"
                        className="text-blue-500 underline"
                    >
                        podmínkami užití
                    </a>{' '}
                    a{' '}
                    <a
                        href={`${API_URL}/../files/vsimavec-zasady-ochrany-osobnich-udaju.pdf`}
                        target="_blank"
                        className="text-blue-500 underline"
                    >
                        zásadami ochrany osobních údajů
                    </a>
                    .
                </>
            ),
        },
        {
            name: 'newsletter_confirmation',
            type: 'checkbox',
            label: (
                <>
                    Chci dostávat informace o preventivních programech a
                    wellbeingu do škol.
                </>
            ),
            required: false,
        },
        // ---------------------------------------------------------------------
        // {
        //     name: 'section-promo',
        //     type: 'heading',
        //     label: 'Promo kód',
        // },
        // {
        //     name: 'section-promo-sub',
        //     type: 'subheading',
        //     label: 'Promo kód není povinný. Pokud jej nemáte, ponechte pole prázdné.',
        // },
        // {
        //     name: 'registration_code',
        //     type: 'text',
        //     label: null,
        //     required: false,
        // },
        { name: 'break', type: 'html', label: <br /> },
    ];

    const touchedAndError =
        Object.keys(errors).length > 0 && Object.keys(touched).length > 0;

    return (
        <>
            {passwordResetStatus !== undefined ? (
                <Alert type="success">{passwordResetStatus}</Alert>
            ) : (
                <div className="w-full max-w-5xl">
                    <form
                        noValidate
                        onSubmit={handleSubmit}
                        className={cn("mx-auto flex w-full max-w-2xl flex-col space-y-4", className)}
                    >
                        {schema.map(
                            renderSchemaForm(
                                values,
                                errors,
                                touched,
                                setFieldValue,
                                setValues,
                                handleChange,
                                handleBlur
                            )
                        )}

                        {touchedAndError ? (
                            <Alert type="error">
                                Formulář obsahuje chyby. Prosím, zkontrolujte a
                                opravte formulář.
                            </Alert>
                        ) : null}

                        <Button
                            type="submit"
                            color="product"
                            isLoading={isSubmitting}
                            disabled={touchedAndError}
                            className="text-base"
                        >
                            Vyzkoušet zdarma
                        </Button>
                    </form>
                </div>
            )}
        </>
    );
}
